'use client';

import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from 'src/components/Button';
import { FormInputNameCreditCard } from 'src/features/userProfile/types';
import type { UseFormReturn } from 'src/hooks';
import type CardClient from 'src/model/CardClient';
import { PaymentTypeUI } from '../PaymentType.ts';
import type PaymentType from 'src/model/PaymentType.js';
import type { Address } from 'src/model/Address';
import { Select } from 'src/components/Select';
import { countryOptions } from 'src/components/Country';

interface PaymentFormUIProps
  extends Pick<UseFormReturn, 'formData' | 'formErrors' | 'onChangeFormInput'> {
  selectedCard?: number;
  shippingCost?: number;
  card?: CardClient[];
  addNewCard: boolean;
  selectedCreditCard: boolean;
  selectedPix: boolean;
  selectedBoleto: boolean;
  onSelectCard: (index: number, cardSelected: CardClient) => void;
  onAddCard: () => void;
  onSubmitRegister: (
    e: FormEvent,
    addressData?: { selectedAddressId?: string; newAddress?: any },
    installmentData?: { installments: number; totalAmount: number }
  ) => void;
  onSelectType: (type: PaymentType) => void;
  totalAmount: number;
  addresses?: Address[];
  selectedAddressId?: string;
  onAddressSelection?: (addressId: string) => void;
  showNewAddressForm?: boolean;
  newAddressData?: any;
  newAddressErrors?: any;
  onChangeNewAddressInput?: any;
  onChangeCEP?: (cep: string) => void;
  selectedInstallments: number;
  onInstallmentChange?: (installments: number) => void;
  voucherAvailable: boolean;
  voucherMessage?: string;
}

export const PaymentFormUI: React.FC<PaymentFormUIProps> = ({
  card,
  formData,
  formErrors,
  selectedCard,
  addNewCard,
  selectedCreditCard,
  selectedPix,
  selectedBoleto,
  selectedInstallments,
  onSelectCard,
  onAddCard,
  onChangeFormInput,
  onSubmitRegister,
  onSelectType,
  totalAmount,
  addresses = [],
  selectedAddressId = '',
  onAddressSelection = () => {},
  showNewAddressForm = false,
  newAddressData = {},
  newAddressErrors = {},
  onChangeNewAddressInput = () => () => {},
  onChangeCEP = () => {},
  onInstallmentChange = () => {},
  shippingCost,
  voucherAvailable,
  voucherMessage,
}) => {
  //const [selectedInstallments, setSelectedInstallments] = useState(1);
  const [isInstallmentExpanded, setIsInstallmentExpanded] = useState(false);

  const minimoParcelamento = 50;
  const MAX_INSTALLMENTS = 6;

  const getInterestRate = (installments: number): number => {
    const rates: Record<number, number> = {
      1: 0,
      2: 0,
      3: 3.49,
      4: 3.99,
      5: 4.49,
      6: 4.99,
    };
    return rates[installments] || 0;
  };

  const money = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const calculateInstallmentValue = (installments: number): number => {
    if (installments === 1) return totalAmount;
    if (installments === 2) return totalAmount / 2;

    const interestRate = getInterestRate(installments);
    console.log(interestRate);

    if (interestRate === 0) {
      return totalAmount / installments;
    }

    const monthlyRate = interestRate / 100;
    const totalWithInterest =
      totalAmount * Math.pow(1 + monthlyRate, installments);
    return totalWithInterest / installments;
  };
  const availableInstallments = useMemo(() => {
    if (totalAmount < minimoParcelamento) return [1];

    return Array.from({ length: MAX_INSTALLMENTS }, (_, i) => i + 1);
  }, [totalAmount, minimoParcelamento]);

  const hasMultipleOptions = availableInstallments.length > 1;

  useEffect(() => {
    if (!availableInstallments.includes(selectedInstallments)) {
      onInstallmentChange(1);
    }
    if (!hasMultipleOptions && isInstallmentExpanded) {
      setIsInstallmentExpanded(false);
    }
  }, [
    availableInstallments,
    hasMultipleOptions,
    isInstallmentExpanded,
    onInstallmentChange,
    selectedInstallments,
  ]);

  const formatCurrency = (value: number): string =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);

  const stateOptions = [
    { value: 'AC', label: 'Acre' },
    { value: 'AL', label: 'Alagoas' },
    { value: 'AP', label: 'Amapá' },
    { value: 'AM', label: 'Amazonas' },
    { value: 'BA', label: 'Bahia' },
    { value: 'CE', label: 'Ceará' },
    { value: 'DF', label: 'Distrito Federal' },
    { value: 'ES', label: 'Espírito Santo' },
    { value: 'GO', label: 'Goiás' },
    { value: 'MA', label: 'Maranhão' },
    { value: 'MT', label: 'Mato Grosso' },
    { value: 'MS', label: 'Mato Grosso do Sul' },
    { value: 'MG', label: 'Minas Gerais' },
    { value: 'PA', label: 'Pará' },
    { value: 'PB', label: 'Paraíba' },
    { value: 'PR', label: 'Paraná' },
    { value: 'PE', label: 'Pernambuco' },
    { value: 'PI', label: 'Piauí' },
    { value: 'RJ', label: 'Rio de Janeiro' },
    { value: 'RN', label: 'Rio Grande do Norte' },
    { value: 'RS', label: 'Rio Grande do Sul' },
    { value: 'RO', label: 'Rondônia' },
    { value: 'RR', label: 'Roraima' },
    { value: 'SC', label: 'Santa Catarina' },
    { value: 'SP', label: 'São Paulo' },
    { value: 'SE', label: 'Sergipe' },
    { value: 'TO', label: 'Tocantins' },
  ];

  const addressOptions = [
    { value: '', label: 'Selecione um endereço' },
    ...addresses.map((addr) => ({
      value: addr.id,
      label: `${addr.street}, ${addr.number} - ${addr.city}/${addr.state} ${addr.main ? '(Principal)' : ''}`,
    })),
    { value: 'new', label: 'Cadastrar novo endereço' },
  ];

  const handleSubmitWithAddress = (e: FormEvent) => {
    e.preventDefault();

    const addressData: { selectedAddressId?: string; newAddress?: any } = {};
    let finalTotalAmount = totalAmount;

    if (selectedCreditCard && selectedInstallments > 1) {
      const interestRate = getInterestRate(selectedInstallments);
      const monthlyRate = interestRate / 100;
      finalTotalAmount =
        totalAmount * Math.pow(1 + monthlyRate, selectedInstallments);
    }

    const installmentData = {
      installments: selectedInstallments,
      totalAmount: finalTotalAmount,
    };

    if (showNewAddressForm) {
      addressData.newAddress = newAddressData;
    } else if (selectedAddressId && selectedAddressId !== 'new') {
      addressData.selectedAddressId = selectedAddressId;
    }

    onSubmitRegister(e, addressData, installmentData);
  };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-red-50 to-red-50 px-6 py-5 border-b border-gray-100">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-2 h-8 bg-gradient-to-b from-red-500 to-red-600 rounded-full"></div>
          Detalhes de Pagamento
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Escolha sua forma de pagamento preferida
        </p>
      </div>

      <div className="p-6">
        <div className="mb-8">
          <PaymentTypeUI
            onSelectType={onSelectType}
            totalAmount={totalAmount}
            voucherAvailable={voucherAvailable}
            voucherMessage={voucherMessage}
          />
        </div>

        {selectedCreditCard && (
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-800 mb-3 sm:mb-0 flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-red-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                  <path
                    fillRule="evenodd"
                    d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                    clipRule="evenodd"
                  />
                </svg>
                Cartão de Crédito
              </h3>
            </div>

            <div className="mb-8 p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200">
              <div className="space-y-4">
                <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  Parcelamento
                </label>

                <div
                  onClick={() => {
                    if (hasMultipleOptions)
                      setIsInstallmentExpanded(!isInstallmentExpanded);
                  }}
                  className={`w-full h-14 px-5 border-2 border-gray-200 rounded-xl bg-white ${
                    hasMultipleOptions
                      ? 'hover:border-red-400 cursor-pointer'
                      : 'cursor-default'
                  } focus-within:border-red-500 focus-within:ring-4 focus-within:ring-red-100 transition-all duration-300 flex items-center justify-between shadow-sm hover:shadow-md`}
                >
                  <div className="flex items-center gap-4">
                    <svg
                      className="w-5 h-5 text-red-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      />
                    </svg>
                    <span className="text-gray-900 font-semibold">
                      {selectedInstallments === 1
                        ? `À vista - ${formatCurrency(totalAmount)}`
                        : `${selectedInstallments}x de ${formatCurrency(calculateInstallmentValue(selectedInstallments))}`}
                    </span>
                  </div>

                  {hasMultipleOptions && (
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${isInstallmentExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  )}
                </div>

                {hasMultipleOptions && isInstallmentExpanded && (
                  <div className="border-2 border-gray-200 rounded-2xl bg-white shadow-xl overflow-hidden">
                    <div className="max-h-80 overflow-y-auto">
                      {availableInstallments.map((installments) => {
                        const installmentValue =
                          calculateInstallmentValue(installments);
                        const interestRate = getInterestRate(installments);
                        const totalWithInterest =
                          installments === 1
                            ? totalAmount
                            : totalAmount *
                              Math.pow(1 + interestRate / 100, installments);

                        return (
                          <div
                            key={installments}
                            onClick={() => {
                              onInstallmentChange(installments);
                              setIsInstallmentExpanded(false);
                            }}
                            className={`p-5 border-b border-gray-100 last:border-b-0 cursor-pointer transition-all duration-300 hover:bg-red-50 ${
                              selectedInstallments === installments
                                ? 'bg-gradient-to-r from-red-50 to-red-50 border-l-4 border-l-red-500'
                                : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <span className="font-bold text-gray-900 text-lg">
                                    {installments === 1
                                      ? 'À vista'
                                      : `${installments}x`}
                                  </span>
                                  {installments <= 2 ? (
                                    <span className="text-xs bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 px-3 py-1 rounded-full font-semibold">
                                      Sem juros
                                    </span>
                                  ) : (
                                    <span className="text-xs bg-gradient-to-r from-orange-100 to-yellow-100 text-orange-700 px-3 py-1 rounded-full font-semibold">
                                      {interestRate}% a.m.
                                    </span>
                                  )}
                                </div>

                                <div className="text-sm text-gray-600 mt-2 font-medium">
                                  {installments === 1
                                    ? formatCurrency(totalAmount)
                                    : `${installments}x de ${formatCurrency(installmentValue)}`}
                                </div>

                                {installments > 2 &&
                                  totalWithInterest > totalAmount && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      Total: {formatCurrency(totalWithInterest)}
                                    </div>
                                  )}
                              </div>

                              {selectedInstallments === installments && (
                                <div className="flex items-center text-red-600">
                                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {selectedInstallments > 2 && (
                  <div className="bg-gradient-to-r from-red-50 to-red-50 border-2 border-red-100 rounded-xl p-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-red-700 font-semibold">
                        Valor total com juros:
                      </span>
                      <span className="font-bold text-red-900">
                        {formatCurrency(
                          totalAmount *
                            Math.pow(
                              1 + getInterestRate(selectedInstallments) / 100,
                              selectedInstallments
                            )
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-red-700 font-semibold">
                        Economia pagando à vista:
                      </span>
                      <span className="font-bold text-green-600">
                        {formatCurrency(
                          totalAmount *
                            Math.pow(
                              1 + getInterestRate(selectedInstallments) / 100,
                              selectedInstallments
                            ) -
                            totalAmount
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mb-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-base text-gray-700">Frete</span>
                <span className="text-base font-semibold text-gray-900">
                  {money(shippingCost || 0)}
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-gray-200 pt-3">
                <span className="text-base text-gray-700">Produtos</span>
                <span className="text-base font-semibold text-gray-900">
                  {money(Math.max((totalAmount || 0) - (shippingCost || 0), 0))}
                </span>
              </div>
            </div>

            {card && card.length > 0 ? (
              <div className="space-y-4 mb-8">
                <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  Cartões Salvos
                </h4>
                <div className="grid gap-3">
                  {card.map((cardItem, index) => (
                    <div
                      key={index}
                      onClick={() => onSelectCard(index, cardItem)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                        selectedCard === index
                          ? 'border-red-500 bg-gradient-to-r from-red-50 to-pink-50 shadow-lg'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 shadow-sm hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-8 bg-gradient-to-r from-red-500 to-red-600 rounded-md flex items-center justify-center">
                            <svg
                              className="w-5 h-5 text-white"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                              <path
                                fillRule="evenodd"
                                d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-bold text-gray-800">
                              •••• •••• •••• {cardItem.cardNumber.slice(-4)}
                            </p>
                            <p className="text-xs text-gray-500 font-medium">
                              Expira em {cardItem.cardExpirationDate}
                            </p>
                          </div>
                        </div>
                        {selectedCard === index && (
                          <div className="flex items-center text-red-600">
                            <div className="w-3 h-3 bg-red-500 rounded-full mr-2 animate-pulse"></div>
                            <span className="text-xs font-bold">
                              Selecionado
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg
                    className="w-6 h-6 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4z" />
                    <path
                      fillRule="evenodd"
                      d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <p className="text-gray-500 font-medium">
                  Nenhum cartão cadastrado
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  Adicione um cartão para continuar
                </p>
              </div>
            )}
            <Button
              buttonStyle="red"
              title="Adicionar Cartão"
              onClick={onAddCard}
              type="button"
              className="w-full sm:w-auto bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 font-semibold py-2 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-md"
            />
          </div>
        )}

        {addNewCard && (
          <div className="border-t-2 border-gray-200 pt-8 mt-8">
            <div className="bg-white border-2 border-gray-200 rounded-3xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-red-50 to-pink-50 border-b-2 border-gray-100 px-8 py-6">
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                  <div className="w-2 h-8 bg-gradient-to-b from-red-500 to-red-600 rounded-full"></div>
                  Adicionar Novo Cartão
                </h3>
                <p className="text-sm text-gray-500 mt-2">
                  Preencha os dados do seu cartão com segurança
                </p>
              </div>

              <div className="px-8 py-8">
                <form onSubmit={handleSubmitWithAddress} className="space-y-8">
                  <div className="space-y-3">
                    <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-red-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                        <path
                          fillRule="evenodd"
                          d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Número do cartão
                    </label>
                    <input
                      type="text"
                      placeholder="1234 5678 9012 3456"
                      value={formData[FormInputNameCreditCard.number]}
                      onChange={(e) =>
                        onChangeFormInput(FormInputNameCreditCard.number)(
                          e.target.value
                        )
                      }
                      className="w-full h-14 px-5 border-2 border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-300 text-gray-900 placeholder-gray-400 font-medium text-lg"
                    />
                    {formErrors.number && (
                      <p className="text-sm text-red-600 font-medium flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {formErrors.number[0]}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-green-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Vencimento
                      </label>
                      <input
                        type="text"
                        placeholder="MM/AA"
                        maxLength={5}
                        value={formData[FormInputNameCreditCard.date]}
                        onChange={(e) =>
                          onChangeFormInput(FormInputNameCreditCard.date)(
                            e.target.value
                          )
                        }
                        className="w-full h-14 px-5 border-2 border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-300 text-gray-900 placeholder-gray-400 font-medium text-lg"
                      />
                      {formErrors.date && (
                        <p className="text-sm text-red-600 font-medium flex items-center gap-1">
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          {formErrors.date[0]}
                        </p>
                      )}
                    </div>

                    <div className="space-y-3">
                      <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-red-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        CVV
                      </label>
                      <input
                        type="text"
                        placeholder="000"
                        value={formData[FormInputNameCreditCard.cvv]}
                        onChange={(e) =>
                          onChangeFormInput(FormInputNameCreditCard.cvv)(
                            e.target.value
                          )
                        }
                        className="w-full h-14 px-5 border-2 border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-300 text-gray-900 placeholder-gray-400 font-medium text-lg"
                      />
                      {formErrors.cvv && (
                        <p className="text-sm text-red-600 font-medium flex items-center gap-1">
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          {formErrors.cvv[0]}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-red-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Nome do titular
                    </label>
                    <input
                      type="text"
                      placeholder="Maria da Silva"
                      value={formData[FormInputNameCreditCard.name]}
                      onChange={(e) =>
                        onChangeFormInput(FormInputNameCreditCard.name)(
                          e.target.value
                        )
                      }
                      className="w-full h-14 px-5 border-2 border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-300 text-gray-900 placeholder-gray-400 font-medium"
                    />
                    {formErrors.name && (
                      <p className="text-sm text-red-600 font-medium flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {formErrors.name[0]}
                      </p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-orange-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      CPF do titular
                    </label>
                    <input
                      type="text"
                      placeholder="123.456.789-00"
                      value={formData[FormInputNameCreditCard.document]}
                      onChange={(e) =>
                        onChangeFormInput(FormInputNameCreditCard.document)(
                          e.target.value
                        )
                      }
                      className="w-full h-14 px-5 border-2 border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-300 text-gray-900 placeholder-gray-400 font-medium"
                    />
                    {formErrors.document && (
                      <p className="text-sm text-red-600 font-medium flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {formErrors.document[0]}
                      </p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-pink-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Nome do cartão (opcional)
                    </label>
                    <input
                      type="text"
                      placeholder="Ex: Cartão Principal"
                      value={formData['label'] || ''}
                      onChange={(e) =>
                        onChangeFormInput('label')(e.target.value)
                      }
                      className="w-full h-14 px-5 border-2 border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-300 text-gray-900 placeholder-gray-400 font-medium"
                    />
                    {formErrors.label && (
                      <p className="text-sm text-red-600 font-medium flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {formErrors.label[0]}
                      </p>
                    )}
                  </div>

                  <div className="border-t-2 border-gray-100 pt-8">
                    <div className="space-y-3">
                      <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-red-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Endereço de Cobrança *
                      </label>
                      <select
                        value={selectedAddressId}
                        onChange={(e) => onAddressSelection(e.target.value)}
                        className="w-full h-14 px-5 border-2 border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-300 text-gray-900 font-medium"
                      >
                        {addressOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {showNewAddressForm && (
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 border-2 border-gray-200">
                      <h4 className="font-bold text-gray-900 mb-6 flex items-center gap-3 text-lg">
                        <svg
                          className="w-6 h-6 text-red-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        Novo Endereço
                      </h4>

                      <div className="space-y-6">
                        <div className="grid grid-cols-2 gap-6">
                          <div className="space-y-3">
                            <label className="text-sm font-bold text-gray-700">
                              CEP
                            </label>
                            <input
                              type="text"
                              placeholder="00000-000"
                              value={newAddressData.zipcode}
                              onChange={(e) => {
                                onChangeNewAddressInput('zipcode')(
                                  e.target.value
                                );
                                onChangeCEP(e.target.value);
                              }}
                              className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl bg-white focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-300 text-gray-900 placeholder-gray-400 font-medium"
                            />
                            {newAddressErrors.zipcode && (
                              <p className="text-sm text-red-600 font-medium">
                                {newAddressErrors.zipcode[0]}
                              </p>
                            )}
                          </div>

                          <div className="space-y-3">
                            <label className="text-sm font-bold text-gray-700">
                              Rua
                            </label>
                            <input
                              type="text"
                              placeholder="Nome da rua"
                              value={newAddressData.street}
                              onChange={(e) =>
                                onChangeNewAddressInput('street')(
                                  e.target.value
                                )
                              }
                              className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl bg-white focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-300 text-gray-900 placeholder-gray-400 font-medium"
                            />
                            {newAddressErrors.street && (
                              <p className="text-sm text-red-600 font-medium">
                                {newAddressErrors.street[0]}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                          <div className="space-y-3">
                            <label className="text-sm font-bold text-gray-700">
                              Número
                            </label>
                            <input
                              type="text"
                              placeholder="123"
                              value={newAddressData.number}
                              onChange={(e) =>
                                onChangeNewAddressInput('number')(
                                  e.target.value
                                )
                              }
                              className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl bg-white focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-300 text-gray-900 placeholder-gray-400 font-medium"
                            />
                            {newAddressErrors.number && (
                              <p className="text-sm text-red-600 font-medium">
                                {newAddressErrors.number[0]}
                              </p>
                            )}
                          </div>

                          <div className="space-y-3">
                            <label className="text-sm font-bold text-gray-700">
                              Bairro
                            </label>
                            <input
                              type="text"
                              placeholder="Nome do bairro"
                              value={newAddressData.district}
                              onChange={(e) =>
                                onChangeNewAddressInput('district')(
                                  e.target.value
                                )
                              }
                              className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl bg-white focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-300 text-gray-900 placeholder-gray-400 font-medium"
                            />
                            {newAddressErrors.district && (
                              <p className="text-sm text-red-600 font-medium">
                                {newAddressErrors.district[0]}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                          <div className="space-y-3">
                            <label className="text-sm font-bold text-gray-700">
                              Cidade
                            </label>
                            <input
                              type="text"
                              placeholder="Nome da cidade"
                              value={newAddressData.city}
                              onChange={(e) =>
                                onChangeNewAddressInput('city')(e.target.value)
                              }
                              className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl bg-white focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-300 text-gray-900 placeholder-gray-400 font-medium"
                            />
                            {newAddressErrors.city && (
                              <p className="text-sm text-red-600 font-medium">
                                {newAddressErrors.city[0]}
                              </p>
                            )}
                          </div>

                          <div className="space-y-3">
                            <label className="text-sm font-bold text-gray-700">
                              Estado
                            </label>
                            <select
                              value={newAddressData.state}
                              onChange={(e) =>
                                onChangeNewAddressInput('state')(e.target.value)
                              }
                              className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl bg-white focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-300 text-gray-900 font-medium"
                            >
                              <option value="">Selecione</option>
                              {stateOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {newAddressErrors.state && (
                              <p className="text-sm text-red-600 font-medium">
                                {newAddressErrors.state[0]}
                              </p>
                            )}
                          </div>
                        </div>
                        <Select
                          name="country"
                          label="País"
                          placeholder="Selecione..."
                          value={newAddressData.country}
                          onChange={(e) =>
                            onChangeFormInput('country')(
                              e?.target.value as string
                            )
                          }
                          className="col-span-2"
                          error={
                            newAddressData.country && newAddressData.country[0]
                          }
                          options={countryOptions}
                          selectClassName="rounded-xl p-4 w-full bg-white border-2 border-gray-200 focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-300 font-medium"
                          optionClassName=""
                        ></Select>
                        <div className="grid grid-cols-2 gap-6 items-end">
                          <div className="space-y-3">
                            <label className="text-sm font-bold text-gray-700">
                              Complemento (opcional)
                            </label>
                            <input
                              type="text"
                              placeholder="Apto 123, Bloco A"
                              value={newAddressData.complement}
                              onChange={(e) =>
                                onChangeNewAddressInput('complement')(
                                  e.target.value
                                )
                              }
                              className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl bg-white focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-300 text-gray-900 placeholder-gray-400 font-medium"
                            />
                          </div>

                          <div className="flex items-center gap-4 h-12">
                            <input
                              type="checkbox"
                              id="main"
                              name="main"
                              checked={newAddressData.main === 'true'}
                              onChange={(e) =>
                                onChangeNewAddressInput('main')(
                                  e.target.checked.toString()
                                )
                              }
                              className="w-5 h-5 text-red-600 border-2 border-gray-300 rounded-md focus:ring-red-500 focus:ring-4"
                            />
                            <label
                              htmlFor="main"
                              className="text-sm font-bold text-gray-700"
                            >
                              Endereço principal
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="pt-6">
                    <button
                      type="submit"
                      disabled={
                        formData[FormInputNameCreditCard.number] === '' ||
                        formData[FormInputNameCreditCard.date] === '' ||
                        formData[FormInputNameCreditCard.cvv] === '' ||
                        formData[FormInputNameCreditCard.name] === '' ||
                        formData[FormInputNameCreditCard.document] === '' ||
                        (!selectedAddressId && !showNewAddressForm)
                      }
                      className="w-full h-14 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white font-bold rounded-2xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Adicionar Cartão
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
